// AddWorkStation01.tsx
import React, { useState, ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { Search, Plus, Minus } from "lucide-react";
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly } from "../../utils/apis.ts";
import { baseurl } from "../../utils/apis.ts";
import { X_API_KEY } from "../../utils/apis.ts";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";
interface WorkInstructionData {
  id: number;
  title: string;
  description: string;
  fileData: string; // This will hold the URL returned after the file upload.
}

interface WorkStationData {
  id: number;
  work_station: string;
  instructions: WorkInstructionData[];
  dropdownOpen: boolean;
}

const AddWorkStation01: React.FC = () => {
  // Extract route parameters.
  const { buildingId, vehicleModelId } = useParams<{ buildingId: string; vehicleModelId: string }>();
  const [workStations, setWorkStations] = useState<WorkStationData[]>([]);
  const [currentWorkStation, setCurrentWorkStation] = useState("");
  const navigate = useNavigate();

  // Add a new workstation.
  const handleAddWorkStation = () => {
    if (currentWorkStation.trim() !== "") {
      const newWS: WorkStationData = {
        id: Date.now(),
        work_station: currentWorkStation,
        instructions: [],
        dropdownOpen: false,
      };
      setWorkStations([...workStations, newWS]);
      setCurrentWorkStation("");
    }
  };

  // Toggle the dropdown for a given workstation.
  const toggleDropdown = (wsId: number) => {
    setWorkStations(prev =>
      prev.map(ws =>
        ws.id === wsId ? { ...ws, dropdownOpen: !ws.dropdownOpen } : ws
      )
    );
  };

  // Add a new empty work instruction row to a workstation.
  const addInstruction = (wsId: number) => {
    setWorkStations(prev =>
      prev.map(ws =>
        ws.id === wsId
          ? {
              ...ws,
              instructions: [
                ...ws.instructions,
                {
                  id: Date.now(), // Using Date.now() as a temporary unique ID.
                  title: "",
                  description: "",
                  fileData: "",
                },
              ],
            }
          : ws
      )
    );
  };

  // Update an instruction’s field.
  const updateInstruction = (
    wsId: number,
    instrId: number,
    field: keyof Omit<WorkInstructionData, "id">,
    value: string
  ) => {

    console.log("updateInstruction", wsId, instrId, field, value);
    

    setWorkStations(prev =>
      prev.map(ws => {
        if (ws.id === wsId) {
          return {
            ...ws,
            instructions: ws.instructions.map(instr =>
              instr.id === instrId ? { ...instr, [field]: value } : instr
            ),
          };
        }
        return ws;
      })
    );
  };

  // Remove an instruction from a workstation.
  const removeInstruction = (wsId: number, instrId: number) => {
    setWorkStations(prev =>
      prev.map(ws => {
        if (ws.id === wsId) {
          return {
            ...ws,
            instructions: ws.instructions.filter(instr => instr.id !== instrId),
          };
        }
        return ws;
      })
    );
  };

  // Handle file input changes for an instruction.
  // Instead of converting the file to base64, we now upload the PDF to the backend.
  // The backend is expected to return a URL string for the uploaded file.
  const handleInstructionFile = async (wsId: number, instrId: number, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await fetch(`${baseurl}/api/upload/workInstruction`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          throw new Error("Failed to upload file");
        }
        const result = await response.json();
      
        updateInstruction(wsId, instrId, "fileData", result.fileUrl);
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }
  };

  // When the user clicks "Done" in the dropdown, post all instructions for that workstation.
  const handleDone = async (wsId: number) => {
    const ws = workStations.find((w) => w.id === wsId);
    if (!ws) return;

    // Post each instruction for this workstation.
    for (const instr of ws.instructions) {
      // Ensure required fields are present.
      if (instr.title.trim() && instr.description.trim() && instr.fileData) {
        const payload = {
          description: instr.description,
          file: instr.fileData,
          model_name: instr.title,
          vehicle_model_id: Number(vehicleModelId),
          work_station_id: ws.id,
        };

        try {
          const response = await fetch(`${baseurl}/api/work_instruction`, {
            method: "POST",
            headers: { "Content-Type": "application/json",
                
             },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            throw new Error(`Failed to add work instruction: ${instr.title}`);
          }
        } catch (error) {
          console.error("Error posting work instruction:", error);
        }
      }
    }
    // Close the dropdown for this workstation.
    setWorkStations(prev =>
      prev.map(wsItem =>
        wsItem.id === wsId ? { ...wsItem, dropdownOpen: false } : wsItem
      )
    );
  };

  // Submit all workstations (post each workstation) then navigate back to building selection.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      for (const ws of workStations) {
        const payload = {
          building_id: Number(buildingId),
          work_station: ws.work_station,
        };
        const response = await fetch("http://172.16.101.250:4500/api/work_station", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`Failed to add work station: ${ws.work_station}`);
        }
      }
      // After saving, navigate back to the building selection page.
      navigate(`/select-building?vehicleModelId=${vehicleModelId}`);
    } catch (error) {
      console.error("Error adding work stations:", error);
    }
  };

  // When the user clicks "Complete Vehicle Model Addition",
  // attach each work instruction (using its id) to its respective workstation.
  const handleCompleteVehicleModel = async () => {
    // Loop through each workstation.
    for (const ws of workStations) {
     
      for (const instr of ws.instructions) {
       
        const payload = {
          work_instruction_id: instr.id,
        };
        try {
          const response = await fetch(
            `http://172.16.101.250:4500/api/work_station/${ws.id}/work_instruction`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          if (!response.ok) {
            console.error(`Failed to attach work instruction ${instr.id} to workstation ${ws.id}`);
          }
        } catch (error) {
          console.error(`Error attaching work instruction ${instr.id} to workstation ${ws.id}:`, error);
        }
      }
    }
    // After attaching all instructions, navigate to the final confirmation page.
    navigate("/admin-vehicle-models");
  };

  return (
    <div className="min-h-screen bg-blue-900 pb-20">
      {/* Header */}
      <div className="p-4">
        <img
          src={digitalOceanImageUrl("new%20logos/Asset%204.png")}
          alt="KMC Logo"
          className="w-16 h-auto ml-auto h-24 w-27"
        />
      </div>
      <div className="p-6 flex justify-between items-center">
        <h1 className="text-white text-lg font-bold">Workstation Creation</h1>
      </div>
      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for an instruction document..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>
      <div className="bg-white rounded-3xl ">
        <div className="mb-6 h-full bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">Add Work Stations</h2>
              <p className="text-gray-600 text-sm max-w-xs">
                Workstations in the selected building are added here.
              </p>
            </div>
            <img
              src={digitalOceanImageUrl("assets/images/machineimage.jpg")}
              alt="Work Station Illustration"
              className="w-27 h-24"
            />
          </div>
        </div>
        <div className="bg-white h-screen p-8 rounded-lg">
          <h1 className="text-2xl font-bold text-blue-900 mb-4">Add Work Stations</h1>
          {/* Input to add a new workstation */}
          <div className="flex mb-4">
            <input
              type="text"
              value={currentWorkStation}
              onChange={(e) => setCurrentWorkStation(e.target.value)}
              placeholder="Enter work station name"
              className="flex-1 p-2 border rounded"
              required
            />
            <button
              type="button"
              onClick={handleAddWorkStation}
              className="ml-2 bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
            >
              Add
            </button>
          </div>
          {/* List of workstations */}
          <ul className="mb-4">
            {workStations.map((ws) => (
              <li key={ws.id} className="p-2 border-b mb-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold">{ws.work_station}</span>
                  <button
                    type="button"
                    onClick={() => toggleDropdown(ws.id)}
                    className="text-blue-900"
                  >
                    {ws.dropdownOpen ? <Minus /> : <Plus />}
                  </button>
                </div>
                {/* Dropdown for adding work instructions */}
                {ws.dropdownOpen && (
                  <div className="mt-4 p-4 border rounded bg-gray-100">
                    {ws.instructions.map((instr) => (
                      <div key={instr.id} className="mb-4 border-b pb-2">
                        <input
                          type="text"
                          value={instr.title}
                          onChange={(e) =>
                            updateInstruction(ws.id, instr.id, "title", e.target.value)
                          }
                          placeholder="Work Instruction Title"
                          className="w-full p-2 border rounded mb-2"
                        />
                        <input
                          type="text"
                          value={instr.description}
                          onChange={(e) =>
                            updateInstruction(ws.id, instr.id, "description", e.target.value)
                          }
                          placeholder="Small Description"
                          className="w-full p-2 border rounded mb-2"
                        />
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={(e) =>
                            handleInstructionFile(ws.id, instr.id, e)
                          }
                          className="w-full mb-2"
                        />
                        <button
                          type="button"
                          onClick={() => removeInstruction(ws.id, instr.id)}
                          className="text-red-500 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addInstruction(ws.id)}
                      className="bg-green-600 text-white px-3 py-1 rounded mr-2"
                    >
                      Add Instruction
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDone(ws.id)}
                      className="bg-blue-900 text-white px-3 py-1 rounded"
                    >
                      Done
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {/* Save and Continue button */}
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-blue-900 text-white px-4 py-2 rounded-3xl hover:bg-blue-800 w-full mb-4"
          >
            Save and Continue
          </button>
          {/* Complete Vehicle Model Addition */}
          <button
            type="button"
            onClick={handleCompleteVehicleModel}
            className="bg-green-700 text-white px-4 py-2 rounded-3xl hover:bg-green-800 w-full"
          >
            Complete Vehicle Model Addition
          </button>
        </div>
        <BottomNav showBack={true} />
      </div>
    </div>
  );
};

export default AddWorkStation01;
